import { useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';
import { CalendarIcon } from 'lucide-react';
import { Control, FieldValues, Path, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import i18n from '@/core/i18n';
import { cn } from '@/lib/utils';
import { normalizeNullableToStartOfDayUTC } from '@/utils/date-formatters/date-formatters';
import { logger } from '@/utils/logger/logger';

import { DateRangeCalendar, DateRangeWithOptionalEnd } from './date-range-calendar';

const DATE_RANGE_PICKER_TAG = 'DateRangePicker:';

// ============================================================================
// Types
// ============================================================================

export interface DateRangePickerProps<T extends FieldValues> {
  /** Form field name */
  name: Path<T>;
  /** React Hook Form control */
  control: Control<T>;
  /** Label displayed above the picker */
  label?: string;
  /** Date format for display (dayjs format) */
  dateFormat?: string;
  /** Controlled open state */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Show "No end date" checkbox option */
  showNoEndDateOption?: boolean;
  /** Disable the picker */
  disabled?: boolean;
  /** Placeholder text when no date is selected */
  placeholder?: string;
  /** Matcher for non-selectable days (e.g. dates before a minimum). */
  disabledDates?: (date: Date) => boolean;
  /** Extra classes for the trigger button (e.g. smaller text on narrow layouts). */
  triggerClassName?: string;
  /** First day of the week in the calendar (0 = Sunday … 1 = Monday). Defaults to Monday. */
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
}

// ============================================================================
// Helper Components
// ============================================================================

interface DisplayTextProps {
  value: DateRangeWithOptionalEnd | null;
  dateFormat: string;
  placeholder: string;
  ongoingText: string;
  /** Whether end date selection is mandatory */
  requiresEndDate?: boolean;
  /** Whether user has started but not completed a range selection */
  isSelectingRange?: boolean;
  selectEndDateText: string;
}

/**
 * Renders the display text for the selected date range.
 * Shows "Select end date" in red when the user has started selecting a range
 * but hasn't picked an end date yet, or when the picker was closed without one.
 */
function DateRangeDisplayText({
  value,
  dateFormat,
  placeholder,
  ongoingText,
  requiresEndDate,
  isSelectingRange,
  selectEndDateText,
}: DisplayTextProps) {
  if (!value?.from) {
    return <>{placeholder}</>;
  }

  const formattedFrom = dayjs(value.from).format(dateFormat);

  // End date is effectively missing when:
  // - `to` is null/undefined (picker was closed without end date), OR
  // - `to` equals `from` AND user is actively selecting a range (first click only)
  const isSameDay = value.to && value.from.getTime() === value.to.getTime();
  const isEndDateMissing = !value.to || (isSelectingRange && isSameDay);

  if (!isEndDateMissing && value.to) {
    const formattedTo = dayjs(value.to).format(dateFormat);
    return i18n.t('common:dateRangeDisplayText', { from: formattedFrom, to: formattedTo });
  }

  // When end date is required but not selected, show prompt in red
  if (requiresEndDate) {
    return (
      <>
        {formattedFrom}
        {' - '}
        <span className="text-destructive">{selectEndDateText}</span>
      </>
    );
  }

  return i18n.t('common:dateRangeDisplayText', { from: formattedFrom, to: ongoingText });
}

interface FooterProps {
  showNoEndDateOption: boolean;
  isNoEndDateChecked: boolean;
  onCheckboxChange: (checked: boolean) => void;
  onApply: () => void;
  isApplyDisabled: boolean;
  noEndDateLabel: string;
  applyLabel: string;
}

/**
 * Renders the footer with optional checkbox and apply button
 */
function CalendarFooter({
  showNoEndDateOption,
  isNoEndDateChecked,
  onCheckboxChange,
  onApply,
  isApplyDisabled,
  noEndDateLabel,
  applyLabel,
}: FooterProps) {
  return (
    <div className="flex items-center justify-between px-4 py-2">
      {showNoEndDateOption ? (
        <div className="flex items-center space-x-2">
          <Checkbox
            id="no-end-date"
            checked={isNoEndDateChecked}
            onCheckedChange={onCheckboxChange}
          />
          <label
            htmlFor="no-end-date"
            className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {noEndDateLabel}
          </label>
        </div>
      ) : (
        <div />
      )}
      <Button
        type="button"
        size="sm"
        variant="default"
        disabled={isApplyDisabled}
        onClick={onApply}
      >
        {applyLabel}
      </Button>
    </div>
  );
}

// ============================================================================
// Hooks
// ============================================================================

interface UsePopoverStateProps {
  controlledOpen?: boolean;
  controlledOnOpenChange?: (open: boolean) => void;
}

/**
 * Manages popover open state (controlled or uncontrolled)
 */
function usePopoverState({ controlledOpen, controlledOnOpenChange }: UsePopoverStateProps) {
  const [internalOpen, setInternalOpen] = useState(false);

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const setOpen = (open: boolean) => {
    controlledOnOpenChange?.(open);
    setInternalOpen(open);
  };

  return { isOpen, setOpen };
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * A date range picker component integrated with React Hook Form.
 *
 * Features:
 * - Single date or date range selection
 * - Optional "No end date" checkbox for ongoing/infinite ranges
 * - Controlled or uncontrolled popover state
 * - Disabled state support
 * - Automatic handling of incomplete ranges on close
 *
 * @example
 * ```tsx
 * <DateRangePicker
 *   name="dateRange"
 *   control={form.control}
 *   showNoEndDateOption={true}
 *   disabled={!hasChanges}
 * />
 * ```
 */
function DateRangePicker<T extends FieldValues>({
  name,
  control,
  label,
  dateFormat = 'MMM D, YYYY',
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  showNoEndDateOption = false,
  disabled = false,
  placeholder,
  disabledDates,
  triggerClassName,
  weekStartsOn,
}: DateRangePickerProps<T>) {
  const { t } = useTranslation('common');
  const { setError, clearErrors, setValue } = useFormContext<T>();

  // Form field value
  const fieldValue = useWatch({ control, name });

  // Popover state management
  const { isOpen, setOpen } = usePopoverState({
    controlledOpen,
    controlledOnOpenChange,
  });

  // "No end date" checkbox state - default checked when option is enabled and no end date
  const [isNoEndDateChecked, setIsNoEndDateChecked] = useState(
    showNoEndDateOption && (!fieldValue?.to || false)
  );

  // Tracks whether user has started selecting a range but hasn't completed it.
  // Used to distinguish "initial same-day range" (valid) from "only start date picked" (incomplete).
  const [isRangeSelectionStarted, setIsRangeSelectionStarted] = useState(false);

  // Guard ref: when true, the next fieldValue?.to change was caused by our own checkbox/calendar
  // handler and should NOT trigger the sync useEffect to re-derive checkbox state.
  const isInternalChangeRef = useRef(false);

  // Sync checkbox state with field value changes
  // When field value updates externally (e.g., from footer's useEffect), update checkbox state.
  // Skip when the change was triggered internally by checkbox or calendar handlers.
  useEffect(() => {
    if (isInternalChangeRef.current) {
      logger.debug(DATE_RANGE_PICKER_TAG, 'Skipping checkbox sync — internal change', {
        fieldTo: fieldValue?.to,
      });
      isInternalChangeRef.current = false;
      return;
    }
    if (showNoEndDateOption) {
      // to === null means "no end date" (Ongoing), to === undefined means "not yet selected"
      // Only sync to checked when to is explicitly null (Ongoing)
      const shouldBeChecked = fieldValue?.to === null;
      logger.debug(DATE_RANGE_PICKER_TAG, 'Syncing checkbox from field value', {
        fieldTo: fieldValue?.to,
        shouldBeChecked,
        currentChecked: isNoEndDateChecked,
      });
      setIsNoEndDateChecked(shouldBeChecked);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValue?.to, showNoEndDateOption]);

  // Translation strings
  const translations = {
    ongoing: t('common:dateRangeLabels.ongoing'),
    selectDates: t('common:dateRangeLabels.selectDates'),
    selectEndDate: t('common:dateRangeLabels.selectEndDate'),
    noEndDate: t('common:dateRangeLabels.noEndDate'),
    apply: t('common:datePicker.apply'),
  };

  // Whether end date is required (no "no end date" option or checkbox is unchecked)
  const isEndDateRequired = !showNoEndDateOption || !isNoEndDateChecked;

  /**
   * Handles closing the popover.
   * When end date is required and not selected, clears `to` and sets a form error.
   * When "No end date" is checked, sets `to` to null (Ongoing).
   */
  const handleClose = (
    fieldOnChange: (value: DateRangeWithOptionalEnd) => void,
    currentValue: DateRangeWithOptionalEnd | null
  ) => {
    logger.debug(DATE_RANGE_PICKER_TAG, 'handleClose called', {
      hasFrom: !!currentValue?.from,
      to: currentValue?.to,
      toIsNull: currentValue?.to === null,
      toIsUndefined: currentValue?.to === undefined,
      isNoEndDateChecked,
      isRangeSelectionStarted,
    });

    // Guard clause: No start date selected
    if (!currentValue?.from) {
      setOpen(false);
      return;
    }

    // When "No end date" is checked, `to` is null — this is a valid complete state
    const hasNoEndDate = currentValue.to === undefined;
    const isSameDayRange =
      currentValue.to && currentValue.from.getTime() === currentValue.to.getTime();

    // Range is incomplete only when:
    // - `to` is undefined (not yet selected — distinct from null which means "Ongoing"), OR
    // - `from === to` AND user actively started a new range selection
    const isIncompleteRange = hasNoEndDate || (isSameDayRange && isRangeSelectionStarted);

    logger.debug(DATE_RANGE_PICKER_TAG, 'handleClose range check', {
      hasNoEndDate,
      isSameDayRange,
      isIncompleteRange,
    });

    // Guard clause: Complete range (or unchanged same-day range), nothing to adjust
    if (!isIncompleteRange) {
      clearErrors(name);
      setOpen(false);
      return;
    }

    // Handle incomplete range in concrete dates only mode:
    // Close the popover, clear `to` without triggering Zod (which would overwrite
    // our manual error with a nested object), then set a readable error message.
    if (!isNoEndDateChecked) {
      isInternalChangeRef.current = true;
      const normalizedFrom = normalizeNullableToStartOfDayUTC(currentValue.from) ?? undefined;
      setValue(name, { from: normalizedFrom, to: undefined } as any, {
        shouldValidate: false,
        shouldDirty: true,
      });
      setError(name, { type: 'manual', message: 'common:dateRangeLabels.endDateRequired' });
      setOpen(false);
      return;
    }

    // Handle incomplete range with "No end date" option
    isInternalChangeRef.current = true;
    const normalizedFrom = normalizeNullableToStartOfDayUTC(currentValue.from) ?? undefined;
    fieldOnChange({ from: normalizedFrom, to: null });
    setIsNoEndDateChecked(true);
    clearErrors(name);
    setOpen(false);
  };

  /**
   * Handles date range changes from the calendar
   */
  const createDateRangeChangeHandler = (
    fieldOnChange: (value: DateRangeWithOptionalEnd | null) => void
  ) => {
    return (dateRange: DateRangeWithOptionalEnd | null) => {
      logger.debug(DATE_RANGE_PICKER_TAG, 'Date range change from calendar', {
        from: dateRange?.from,
        to: dateRange?.to,
        toIsNull: dateRange?.to === null,
        isNoEndDateChecked,
        isRangeSelectionStarted,
      });

      if (!dateRange) {
        fieldOnChange(null);
        return;
      }

      // Normalize dates to UTC midnight to avoid timezone issues
      // Convert null to undefined for 'from' field (DateRangeWithOptionalEnd expects Date | undefined for 'from')
      const normalizedFrom = normalizeNullableToStartOfDayUTC(dateRange.from) ?? undefined;
      // 'to' can be null (for ongoing) or Date or undefined
      const normalizedTo = normalizeNullableToStartOfDayUTC(dateRange.to);

      // When "No end date" is checked, only update start date
      if (isNoEndDateChecked) {
        isInternalChangeRef.current = true;
        fieldOnChange({ from: normalizedFrom, to: null });
        setIsRangeSelectionStarted(false);
        clearErrors(name);
      } else {
        // Track range selection state:
        // Same day (from === to) on the FIRST click means user just picked the start date.
        // Same day on the SECOND click (isRangeSelectionStarted already true) means user
        // confirmed a single-day range by clicking the same date again.
        // Different dates always mean user completed the range selection.
        const isSameDay =
          normalizedFrom && normalizedTo && normalizedFrom.getTime() === normalizedTo.getTime();

        logger.debug(DATE_RANGE_PICKER_TAG, 'Range selection logic', {
          isSameDay,
          isRangeSelectionStarted,
          normalizedFrom: normalizedFrom?.toISOString(),
          normalizedTo: normalizedTo?.toISOString(),
        });

        isInternalChangeRef.current = true;
        if (isSameDay && isRangeSelectionStarted) {
          // Second click on the same date — confirm single-day range
          fieldOnChange({ from: normalizedFrom, to: normalizedTo ?? undefined });
          setIsRangeSelectionStarted(false);
          clearErrors(name);
        } else if (isSameDay) {
          // First click — start of range selection.
          // Set `to` to undefined so that Zod validation marks the form as invalid
          // immediately, preventing submission while end date is missing.
          fieldOnChange({ from: normalizedFrom, to: undefined });
          setIsRangeSelectionStarted(true);
        } else if (normalizedFrom && normalizedTo) {
          fieldOnChange({ from: normalizedFrom, to: normalizedTo ?? undefined });
          setIsRangeSelectionStarted(false);
          clearErrors(name);
        } else {
          fieldOnChange({ from: normalizedFrom, to: normalizedTo ?? undefined });
        }
      }
    };
  };

  /**
   * Handles "No end date" checkbox changes
   */
  const createCheckboxChangeHandler = (
    fieldOnChange: (value: DateRangeWithOptionalEnd) => void,
    currentValue: DateRangeWithOptionalEnd | null
  ) => {
    return (checked: boolean) => {
      logger.debug(DATE_RANGE_PICKER_TAG, 'Checkbox changed', {
        checked,
        currentFrom: currentValue?.from,
        currentTo: currentValue?.to,
      });

      // Mark as internal change so the sync useEffect doesn't override our checkbox state
      isInternalChangeRef.current = true;
      setIsNoEndDateChecked(checked);

      if (checked) {
        // Set end date to null (Ongoing)
        const normalizedFrom = normalizeNullableToStartOfDayUTC(currentValue?.from) ?? undefined;
        fieldOnChange({ from: normalizedFrom, to: null });
      } else {
        // Clear end date so user must select one
        if (currentValue?.from) {
          const normalizedFrom = normalizeNullableToStartOfDayUTC(currentValue.from) ?? undefined;
          fieldOnChange({ from: normalizedFrom, to: undefined });
        }
      }
    };
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const handleDateRangeChange = createDateRangeChangeHandler(field.onChange);
        const handleCheckboxChange = createCheckboxChangeHandler(
          field.onChange,
          field.value || null
        );
        // Apply is disabled when no start date, or when end date is required but not yet selected
        const currentValue = (field.value || null) as DateRangeWithOptionalEnd | null;
        // to === null means "Ongoing" (valid), to === undefined means "not yet selected" (invalid)
        const isMissingEndDate =
          isEndDateRequired && currentValue?.from && currentValue?.to === undefined;
        const isOnlyStartSelected =
          isEndDateRequired &&
          isRangeSelectionStarted &&
          currentValue?.from &&
          currentValue?.to &&
          currentValue.from.getTime() === currentValue.to.getTime();
        const isApplyButtonDisabled =
          !fieldValue?.from || !!isMissingEndDate || !!isOnlyStartSelected;

        const handleApply = () => {
          logger.debug(DATE_RANGE_PICKER_TAG, 'Apply clicked', {
            from: currentValue?.from,
            to: currentValue?.to,
            isNoEndDateChecked,
          });
          handleClose(field.onChange, currentValue);
        };
        const handlePopoverOpenChange = (open: boolean) => {
          logger.debug(DATE_RANGE_PICKER_TAG, 'Popover open change', { open, disabled });
          if (disabled) return;
          if (open) {
            setIsRangeSelectionStarted(false);
            clearErrors(name);
            setOpen(true);
          } else {
            handleClose(field.onChange, field.value || null);
          }
        };

        return (
          <FormItem>
            <FormControl>
              <div className="grid gap-2">
                {label && <FormLabel htmlFor={name}>{label}</FormLabel>}

                <Popover open={disabled ? false : isOpen} onOpenChange={handlePopoverOpenChange}>
                  <PopoverTrigger asChild>
                    <Button
                      id="date"
                      variant="outline"
                      disabled={disabled}
                      className={cn(
                        'w-full min-w-0 justify-start text-left font-normal',
                        // Match the disabled SingleSelect look (muted box, full
                        // opacity, readable text) so disabled fields stay consistent.
                        'disabled:border-input disabled:bg-muted disabled:opacity-100 disabled:pointer-events-none disabled:text-foreground',
                        !fieldValue?.from && 'text-muted-foreground',
                        triggerClassName
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                      <span className="min-w-0 flex-1 truncate">
                        <DateRangeDisplayText
                          value={fieldValue}
                          dateFormat={dateFormat}
                          placeholder={placeholder || translations.selectDates}
                          ongoingText={translations.ongoing}
                          requiresEndDate={isEndDateRequired}
                          isSelectingRange={isRangeSelectionStarted}
                          selectEndDateText={translations.selectEndDate}
                        />
                      </span>
                    </Button>
                  </PopoverTrigger>

                  <PopoverContent className="w-auto p-0" align="start">
                    <DateRangeCalendar
                      onChange={handleDateRangeChange}
                      numberOfmonths={1}
                      value={fieldValue}
                      disabled={disabledDates}
                      weekStartsOn={weekStartsOn}
                      disableRangeSelection={showNoEndDateOption && isNoEndDateChecked}
                      footer={
                        <CalendarFooter
                          showNoEndDateOption={showNoEndDateOption}
                          isNoEndDateChecked={isNoEndDateChecked}
                          onCheckboxChange={handleCheckboxChange}
                          onApply={handleApply}
                          isApplyDisabled={isApplyButtonDisabled}
                          noEndDateLabel={translations.noEndDate}
                          applyLabel={translations.apply}
                        />
                      }
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

export { DateRangePicker };
